import { Loader2, Send } from "lucide-react";
import Button3D from "@/components/orders/Button3D";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TEMPLATE_LABEL, describeProductLine } from "./templates";
import type { MailDraft } from "./types";

interface MailDraftEditorProps {
  drafts: MailDraft[];
  isSending: boolean;
  onDraftChange: (index: number, patch: Partial<Pick<MailDraft, "subject" | "body">>) => void;
  onBack: () => void;
  onSend: () => void;
}

export const MailDraftEditor = ({
  drafts,
  isSending,
  onDraftChange,
  onBack,
  onSend,
}: MailDraftEditorProps) => {
  const totalProducts = drafts.reduce((sum, draft) => sum + draft.productLines.length, 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 p-3">
        <div className="text-sm text-foreground">
          {drafts.length} {drafts.length === 1 ? "mail" : "mails"} voor {totalProducts}{" "}
          {totalProducts === 1 ? "product" : "producten"}
        </div>
      </div>

      <div className="mt-3 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        {drafts.map((draft, index) => (
          <div
            key={`${draft.template_key}-${index}`}
            className="space-y-3 rounded-md border bg-background p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="text-[10px]">
                {TEMPLATE_LABEL[draft.template_key]}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {draft.productLines.length}{" "}
                {draft.productLines.length === 1 ? "product" : "producten"}
              </span>
            </div>

            <ul className="space-y-1 pl-4 text-xs text-muted-foreground">
              {draft.productLines.map((productLine) => (
                <li key={productLine.id} className="list-disc">
                  {describeProductLine(productLine)}
                </li>
              ))}
            </ul>

            <div className="space-y-1.5">
              <Label htmlFor={`draft-subject-${index}`}>Onderwerp</Label>
              <Input
                id={`draft-subject-${index}`}
                value={draft.subject}
                onChange={(event) => onDraftChange(index, { subject: event.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`draft-body-${index}`}>Bericht</Label>
              <Textarea
                id={`draft-body-${index}`}
                value={draft.body}
                onChange={(event) => onDraftChange(index, { body: event.target.value })}
                className="min-h-[220px] text-xs"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t bg-background pt-3">
        <Button3D tone="neutral" onClick={onBack} disabled={isSending}>
          Annuleren
        </Button3D>
        <Button3D tone="primary" onClick={onSend} disabled={isSending || drafts.length === 0}>
          {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Versturen naar testadres
        </Button3D>
      </div>
    </div>
  );
};
